import React, { Component } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    TextField,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Box,
    CircularProgress,
    MenuItem,
    withStyles
} from '@material-ui/core';
import {
    Business as BusinessIcon,
    AddCircleOutline as AddIcon
} from '@material-ui/icons';
import { Autocomplete, TextField as MuiTextField } from '@mui/material';
import CustomButton from '../../shared/components/common/customButton';
import { CnpjInput } from '../../../components/cnpjInput';
import { PhoneInput } from '../../../components/phoneInput';
import { StringService } from '../../../utils/stringService';
import { CompanyGroupService } from '../../../companyGroup/companyGroupService';
import { WorkshopQuotationsService } from '../workshopQuotations/service';
import { CatalogHierarchyService } from '../../shared/service/catalogHierarchyService';

function generateSchemaName(companyName) {
    if (!companyName) return '';
    return companyName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
}

const STEPS = {
    COMPANY_STEP: 0,
    QUOTATION_STEP: 1,
};

const COMPANY_VIEWS = {
    CHOICE: 'CHOICE',
    CREATE_NEW: 'CREATE_NEW',
    SELECT_EXISTING: 'SELECT_EXISTING',
};

const styles = () => ({
    stepperRoot: {
        backgroundColor: 'transparent',
        padding: '16px 24px 8px',
    },
    dialogTitle: {
        padding: '16px 24px 0',
        display: 'flex',
        alignItems: 'center',
    },
    backButton: {
        marginRight: '8px',
        padding: '8px',
    },
    formRow: {
        display: 'flex',
        gap: '16px',
        '& > *': {
            flex: 1,
        },
    },
    schemaPreview: {
        marginTop: '12px',
        padding: '10px 14px',
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        border: '1px solid #e0e0e0',
    },
    schemaLabel: {
        fontSize: '0.75rem',
        color: '#666',
        marginBottom: 2,
    },
    schemaValue: {
        fontSize: '0.95rem',
        fontFamily: 'monospace',
        color: '#1b5e20',
        fontWeight: 600,
    },
    submittingBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        gap: '16px',
    },
    quotationInfoCard: {
        backgroundColor: '#e8f5e9',
        borderRadius: 6,
        padding: '12px 16px',
        marginBottom: '16px',
        border: '1px solid #c8e6c9',
    },
    quotationInfoLabel: {
        fontSize: '0.75rem',
        color: '#666',
        fontWeight: 500,
    },
    quotationInfoValue: {
        fontSize: '0.9rem',
        color: '#333',
        fontWeight: 600,
    },
    sectionTitle: {
        fontSize: '0.85rem',
        fontWeight: 600,
        color: '#333',
        marginTop: '12px',
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    choiceCardsContainer: {
        display: 'flex',
        gap: '24px',
        marginTop: '24px',
        marginBottom: '24px',
    },
    choiceCard: {
        flex: 1,
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'center',
        backgroundColor: '#fff',
        '&:hover': {
            borderColor: '#198754',
            backgroundColor: '#f8fcfa',
            boxShadow: '0 4px 12px rgba(25, 135, 84, 0.1)',
        },
    },
    choiceIcon: {
        fontSize: '48px',
        color: '#198754',
        marginBottom: '16px',
    },
    choiceTitle: {
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#333',
        marginBottom: '8px',
    },
    choiceDesc: {
        fontSize: '0.85rem',
        color: '#666',
    }
});

class QuickCompanyModal extends Component {
    constructor(props) {
        super(props);
        this.state = this.getInitialState();
    }

    getInitialState = () => ({
        activeStep: STEPS.COMPANY_STEP,
        companyView: this.props.mode === 'new' ? COMPANY_VIEWS.CREATE_NEW : COMPANY_VIEWS.SELECT_EXISTING,
        submitting: false,
        // Create Company fields
        companyCnpj: '__.___.___/____.__',
        companyName: '',
        responsibleName: '',
        responsibleEmail: '',
        responsiblePhone: '(__) ____-____',
        // Select Existing Company fields
        linkedClients: [],
        loadingLinkedClients: false,
        selectedLinkedClientId: '',
        companySearchFilter: '',
        // Result fields
        createdCompanyId: null,
        createdCompanyGroup: '',
        // Hierarchical Quotation fields
        categorias: [],
        produtos: [],
        servicos: [],
        loadingCategorias: false,
        loadingProdutos: false,
        loadingServicos: false,
        categoriaId: '',
        produtoId: '',
        servicoId: '',
        servicoSelecionado: null,
        quantity: 1,
        model: '',
        brand: '',
        observacoes: '',
    });

    componentDidUpdate(prevProps, prevState) {
        // On open: reset to the mode's initial view and (for existing mode) load linked clients.
        if (this.props.open && !prevProps.open) {
            this.setState(this.getInitialState(), () => {
                if (this.props.mode !== 'new') {
                    this.fetchLinkedClients();
                }
            });
            return;
        }
        if (this.state.companyView === COMPANY_VIEWS.SELECT_EXISTING && prevState.companyView !== COMPANY_VIEWS.SELECT_EXISTING) {
            this.fetchLinkedClients();
        }
    }

    resetState = () => {
        this.setState(this.getInitialState());
    };

    handleClose = () => {
        this.resetState();
        this.props.onClose();
    };

    updateField = (field, value) => {
        this.setState({ [field]: value });
    };

    goBackToChoice = () => {
        this.setState({ companyView: COMPANY_VIEWS.CHOICE });
    }

    fetchLinkedClients = () => {
        this.setState({ loadingLinkedClients: true });
        WorkshopQuotationsService.getLinkedClients()
            .then(data => {
                this.setState({ linkedClients: data || [], loadingLinkedClients: false });
            })
            .catch(error => {
                this.setState({ loadingLinkedClients: false });
                this.props.enqueueSnackbar('Erro ao buscar empresas vinculadas.', { variant: 'error' });
            });
    }

    loadCategorias = async () => {
        this.setState({ loadingCategorias: true });
        try {
            const data = await CatalogHierarchyService.getCategorias();
            this.setState({ categorias: data, loadingCategorias: false });
        } catch (error) {
            this.setState({ loadingCategorias: false });
            console.error('Erro ao buscar categorias:', error);
        }
    };

    handleCategoriaChange = async (event) => {
        const catId = event.target.value;
        this.setState({
            categoriaId: catId,
            produtoId: '',
            servicoId: '',
            servicoSelecionado: null,
            produtos: [],
            servicos: []
        });

        if (!catId) return;

        this.setState({ loadingProdutos: true });
        try {
            const data = await CatalogHierarchyService.getProdutosByCategoria(catId);
            this.setState({ produtos: data, loadingProdutos: false });
        } catch (err) {
            this.setState({ loadingProdutos: false });
            console.error('Erro ao buscar produtos:', err);
        }
    };

    handleProdutoChange = async (event) => {
        const prodId = event.target.value;
        this.setState({
            produtoId: prodId,
            servicoId: '',
            servicoSelecionado: null,
            servicos: []
        });

        if (!prodId) return;

        this.setState({ loadingServicos: true });
        try {
            const data = await CatalogHierarchyService.getServicosByProduto(prodId);
            this.setState({ servicos: data, loadingServicos: false });
        } catch (err) {
            this.setState({ loadingServicos: false });
            console.error('Erro ao buscar serviços:', err);
        }
    };

    handleServicoChange = (event) => {
        const srvId = event.target.value;
        const { servicos, categoriaId, produtoId } = this.state;
        
        const selected = servicos.find(s => String(s.id) === String(srvId));
        this.setState({
            servicoId: srvId,
            servicoSelecionado: selected ? {
                id: selected.id,
                nome: selected.nome,
                descricao: selected.nome, // para fins de compatibilidade
                valor: selected.valor || 0,
                categoria_id: categoriaId,
                produto_id: produtoId
            } : null
        });
    };

    // --- Step 1: Create Company Submit ---
    handleCreateCompanySubmit = () => {
        const { enqueueSnackbar, currentLanguage } = this.props;
        const { companyCnpj, companyName, responsibleName, responsibleEmail, responsiblePhone } = this.state;
        const lang = currentLanguage.bepiService.workshop;

        if (!companyName.trim() || !responsibleName.trim() || !responsibleEmail.trim()) {
            enqueueSnackbar(lang.workshop_quick_error_missing, { variant: 'error' });
            return;
        }

        const schemaName = generateSchemaName(companyName);
        if (!schemaName) {
            enqueueSnackbar(lang.workshop_quick_error_invalid, { variant: 'error' });
            return;
        }

        this.setState({ submitting: true });

        const companyGroupData = {
            companyCnpj: StringService.extractDigits(companyCnpj) || '',
            companyName: companyName.trim(),
            companyGroup: schemaName,
            companyTagsLimit: 0,
            responsibleName: responsibleName.trim(),
            responsibleEmail: responsibleEmail.trim(),
            responsiblePhone: StringService.extractDigits(responsiblePhone) || '',
            responsibleOccupation: 'USER',
            responsiblePassword: 'Mudar-00',
        };

        CompanyGroupService.create(companyGroupData)
            .then(async (result) => {
                let createdId = null;

                // Extract directly from creation response
                if (result && result.companies && result.companies.length > 0) {
                    const matched = result.companies.find(c => c.name === companyName.trim()) || result.companies[0];
                    createdId = matched.id;
                }

                // If backend didn't return the ID explicitly for some reason, fetch the list and find it
                if (!createdId) {
                    try {
                        const allCompanies = await CompanyGroupService.list();
                        const foundGroup = allCompanies.find(c => c.name === schemaName);
                        if (foundGroup && foundGroup.companies && foundGroup.companies.length > 0) {
                            const matchedCompany = foundGroup.companies.find(comp => comp.name === companyName.trim())
                                || foundGroup.companies[0];
                            createdId = matchedCompany.id;
                        }
                    } catch (e) {
                        console.warn('Could not fetch company list to resolve ID', e);
                    }
                }

                if (createdId) {
                    // Automatically link client to workshop in the backend
                    try {
                        await WorkshopQuotationsService.linkClient({
                            cliente_database: `mobcheck_${schemaName}`,
                            cliente_name: companyName.trim(),
                            cliente_company_id: createdId,
                            cliente_user_name: responsibleName.trim(),
                            cliente_cnpj: StringService.extractDigits(companyCnpj) || ''
                        });
                    } catch (e) {
                        console.error('Failed to link client to workshop', e);
                    }
                }

                enqueueSnackbar(lang.workshop_quick_company_success, { variant: 'success' });

                // Admin "Cadastrar Cliente" flow: company registration only, no quotation step.
                if (this.props.mode === 'new') {
                    this.setState({ submitting: false });
                    this.resetState();
                    this.props.onClose();
                    if (this.props.onSuccess) this.props.onSuccess();
                    return;
                }

                this.setState({
                    submitting: false,
                    activeStep: STEPS.QUOTATION_STEP,
                    createdCompanyId: createdId,
                    createdCompanyGroup: schemaName,
                });
                this.loadCategorias();
            })
            .catch((error) => {
                this.setState({ submitting: false });
                const errorCode = error?.response?.data;
                if (errorCode === 'CREATE_MISSING_FIELD') {
                    enqueueSnackbar(lang.workshop_quick_error_missing, { variant: 'error' });
                } else if (errorCode === 'CREATE_EXISTING_COMPANY_GROUP') {
                    enqueueSnackbar(lang.workshop_quick_error_exists, { variant: 'error' });
                } else if (errorCode === 'CREATE_BAD_COMPANY_GROUP_NAME') {
                    enqueueSnackbar(lang.workshop_quick_error_invalid, { variant: 'error' });
                } else if (errorCode === 'USER_REGISTER_EMAIL_EXISTS') {
                    enqueueSnackbar(lang.workshop_quick_error_email_exists, { variant: 'error' });
                } else {
                    enqueueSnackbar(lang.workshop_quick_error_generic || 'Erro ao criar empresa.', { variant: 'error' });
                }
            });
    };

    // --- Step 1: Select Existing Company Submit ---
    handleSelectExistingSubmit = () => {
        const { selectedLinkedClientId, linkedClients } = this.state;
        const { enqueueSnackbar, currentLanguage } = this.props;
        const lang = currentLanguage.bepiService.workshop;

        if (!selectedLinkedClientId) {
            enqueueSnackbar(lang.workshop_quick_error_missing || 'Selecione uma empresa', { variant: 'error' });
            return;
        }

        const selectedClient = linkedClients.find(c => String(c.id) === String(selectedLinkedClientId));

        if (selectedClient) {
            this.setState({
                activeStep: STEPS.QUOTATION_STEP,
                createdCompanyId: selectedClient.cliente_company_id,
                companyName: selectedClient.cliente_name,
                createdCompanyGroup: selectedClient.cliente_database?.replace('mobcheck_', '') || '',
                // Resetting CNPJ as bs_cliente_prestador does not store it
                companyCnpj: ''
            });
            this.loadCategorias();
        }
    }

    // --- Step 2: Quotation Submit ---
    handleQuotationSubmit = () => {
        const { enqueueSnackbar, currentLanguage, onSuccess } = this.props;
        const {
            createdCompanyId, companyName, companyCnpj, createdCompanyGroup,
            servicoSelecionado,
            quantity, model, brand, observacoes,
        } = this.state;
        const lang = currentLanguage.bepiService.workshop;

        if (!servicoSelecionado) {
            enqueueSnackbar(lang.workshop_quick_error_missing || 'Selecione a Categoria, Produto e Serviço', { variant: 'error' });
            return;
        }

        this.setState({ submitting: true });

        const equipamentos = [{ 
            id: servicoSelecionado.id, 
            nome: servicoSelecionado.nome,
            servico_id: servicoSelecionado.id,
            categoria_id: servicoSelecionado.categoria_id,
            produto_id: servicoSelecionado.produto_id
        }];

        const servicos = [{ 
            id: servicoSelecionado.id, 
            nome: servicoSelecionado.nome, 
            valor: servicoSelecionado.valor || 0,
            categoria_id: servicoSelecionado.categoria_id,
            produto_id: servicoSelecionado.produto_id
        }];

        const itensPayload = {
            cliente: {
                empresa: companyName,
                nome: this.state.responsibleName || '',
                cnpj: companyCnpj && companyCnpj.includes('_') ? '' : (StringService.extractDigits(companyCnpj) || ''),
                responsavel: this.state.responsibleName || '',
                email: this.state.responsibleEmail || '',
                telefone: this.state.responsiblePhone && this.state.responsiblePhone.includes('_') ? '' : (StringService.extractDigits(this.state.responsiblePhone) || ''),
            },
            equipamentos,
            servicos,
            quantity: Number(quantity) || 1,
            model: model || '',
            brand: brand || '',
            observacoes: observacoes || '',
            maintenanceLocalization: 'oficina',
            endereco: 'Oficina',
        };

        const quotationData = {
            tipo: 'estimativa',
            company_id: createdCompanyId,
            client_database: `mobcheck_${createdCompanyGroup}`,
            status: 'equipamento em analise',
            itens: JSON.stringify(itensPayload),
        };

        // Use Workshop specifically routed create endpoint
        WorkshopQuotationsService.createQuotationFromWorkshop(quotationData)
            .then(() => {
                enqueueSnackbar(lang.workshop_quick_quotation_success, { variant: 'success' });
                this.resetState();
                this.props.onClose();
                if (onSuccess) onSuccess();
            })
            .catch((error) => {
                this.setState({ submitting: false });
                enqueueSnackbar(
                    error?.message || lang.workshop_quick_error_generic || 'Erro ao criar cotação.',
                    { variant: 'error' }
                );
            });
    };

    // --- Renderers ---

    renderCompanyChoice = () => {
        const { classes, currentLanguage } = this.props;
        const lang = currentLanguage.bepiService.workshop;

        return (
            <DialogContent>
                <Typography variant="body1" align="center" style={{ color: '#666', marginBottom: '8px' }}>
                    {lang.workshop_quick_choice_subtitle || 'Escolha como deseja iniciar o atendimento'}
                </Typography>
                <Box className={classes.choiceCardsContainer}>
                    <Box
                        className={classes.choiceCard}
                        onClick={() => this.setState({ companyView: COMPANY_VIEWS.SELECT_EXISTING })}
                    >
                        <BusinessIcon className={classes.choiceIcon} />
                        <Typography className={classes.choiceTitle}>
                            {lang.workshop_quick_choice_existing || 'Empresa Existente'}
                        </Typography>
                        <Typography className={classes.choiceDesc}>
                            {lang.workshop_quick_choice_existing_desc || 'Selecione uma empresa já cadastrada'}
                        </Typography>
                    </Box>
                    <Box
                        className={classes.choiceCard}
                        onClick={() => this.setState({ companyView: COMPANY_VIEWS.CREATE_NEW })}
                    >
                        <AddIcon className={classes.choiceIcon} />
                        <Typography className={classes.choiceTitle}>
                            {lang.workshop_quick_choice_new || 'Nova Empresa'}
                        </Typography>
                        <Typography className={classes.choiceDesc}>
                            {lang.workshop_quick_choice_new_desc || 'Cadastre um novo cliente rapidamente'}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
        );
    }

    renderSelectExistingForm = () => {
        const { currentLanguage, classes } = this.props;
        const { linkedClients, selectedLinkedClientId, loadingLinkedClients } = this.state;
        const lang = currentLanguage.bepiService.workshop;

        if (loadingLinkedClients) {
            return (
                <DialogContent>
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                        <CircularProgress size={36} style={{ color: '#1b5e20' }} />
                    </Box>
                </DialogContent>
            );
        }

        const selectedOption = linkedClients.find(c => String(c.id) === String(selectedLinkedClientId)) || null;

        return (
            <DialogContent>
                <Box style={{ padding: '24px 0' }}>
                    <Autocomplete
                        options={linkedClients}
                        getOptionLabel={(option) => `${option.cliente_name} ${option.cliente_cnpj ? ` - CNPJ: ${option.cliente_cnpj}` : ''}`}
                        value={selectedOption}
                        onChange={(event, newValue) => {
                            this.updateField('selectedLinkedClientId', newValue ? newValue.id : '');
                        }}
                        filterOptions={(options, state) => {
                            const term = state.inputValue.toLowerCase();
                            if (!term) return options;
                            const termDigits = StringService.extractDigits(term);
                            return options.filter(client => {
                                const matchName = client.cliente_name && client.cliente_name.toLowerCase().includes(term);
                                const matchCnpj = termDigits && client.cliente_cnpj && StringService.extractDigits(client.cliente_cnpj).includes(termDigits);
                                return matchName || matchCnpj;
                            });
                        }}
                        renderInput={(params) => (
                            <MuiTextField
                                {...params}
                                label={lang.workshop_quick_select_company || 'Buscar Empresa (Nome ou CNPJ)'}
                                variant="outlined"
                                margin="normal"
                                placeholder="Digite para filtrar..."
                            />
                        )}
                        noOptionsText="Nenhuma empresa encontrada"
                    />
                </Box>
            </DialogContent>
        );
    }

    renderCreateCompanyForm = () => {
        const { currentLanguage, classes } = this.props;
        const { companyCnpj, companyName, responsibleName, responsibleEmail, responsiblePhone } = this.state;
        const lang = currentLanguage.bepiService.workshop;
        const schemaPreview = generateSchemaName(companyName);

        return (
            <DialogContent>
                <Box className={classes.formRow}>
                    <TextField
                        margin="normal"
                        label={lang.workshop_quick_cnpj}
                        InputProps={{ inputComponent: CnpjInput }}
                        value={companyCnpj}
                        onFocus={(e) => e.target.setSelectionRange(0, -1)}
                        onChange={(e) => this.updateField('companyCnpj', e.target.value)}
                    />
                    <TextField
                        required
                        margin="normal"
                        label={lang.workshop_quick_company_name}
                        inputProps={{ maxLength: 255 }}
                        value={companyName}
                        onChange={(e) => this.updateField('companyName', e.target.value)}
                    />
                </Box>
                <Box className={classes.formRow}>
                    <TextField
                        required
                        margin="normal"
                        label={lang.workshop_quick_responsible}
                        inputProps={{ maxLength: 40 }}
                        value={responsibleName}
                        onChange={(e) => this.updateField('responsibleName', e.target.value)}
                    />
                    <TextField
                        required
                        margin="normal"
                        label={lang.workshop_quick_email}
                        value={responsibleEmail}
                        onChange={(e) => this.updateField('responsibleEmail', e.target.value)}
                    />
                </Box>
                <TextField
                    margin="normal"
                    label={lang.workshop_quick_phone}
                    InputProps={{ inputComponent: PhoneInput }}
                    value={responsiblePhone}
                    fullWidth
                    style={{ maxWidth: 'calc(50% - 8px)' }}
                    onFocus={(e) => e.target.setSelectionRange(0, -1)}
                    onChange={(e) => this.updateField('responsiblePhone', e.target.value)}
                />
                {companyName.trim() && (
                    <Box className={classes.schemaPreview}>
                        <Typography className={classes.schemaLabel}>
                            {lang.workshop_quick_schema_preview}
                        </Typography>
                        <Typography className={classes.schemaValue}>
                            mobcheck_{schemaPreview}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        );
    };

    renderQuotationForm = () => {
        const { currentLanguage, classes } = this.props;
        const {
            companyName, companyCnpj,
            loadingCategorias, loadingProdutos, loadingServicos,
            categorias, produtos, servicos,
            categoriaId, produtoId, servicoId,
            quantity, model, brand, observacoes,
        } = this.state;
        const lang = currentLanguage.bepiService.workshop;

        if (loadingCategorias) {
            return (
                <DialogContent>
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                        <CircularProgress size={36} style={{ color: '#1b5e20' }} />
                    </Box>
                </DialogContent>
            );
        }

        return (
            <DialogContent>
                {/* Company summary card */}
                <Box className={classes.quotationInfoCard}>
                    <Box className={classes.formRow}>
                        <Box>
                            <Typography className={classes.quotationInfoLabel}>
                                {lang.workshop_quick_company_name}
                            </Typography>
                            <Typography className={classes.quotationInfoValue}>
                                {companyName}
                            </Typography>
                        </Box>
                        {companyCnpj && !companyCnpj.includes('_') && (
                            <Box>
                                <Typography className={classes.quotationInfoLabel}>
                                    {lang.workshop_quick_cnpj}
                                </Typography>
                                <Typography className={classes.quotationInfoValue}>
                                    {companyCnpj}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography className={classes.quotationInfoLabel}>
                                {lang.workshop_quick_local}
                            </Typography>
                            <Typography className={classes.quotationInfoValue}>
                                Oficina
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Equipment + Service selectors */}
                <Typography className={classes.sectionTitle}>
                    {lang.workshop_quick_equip_service_title}
                </Typography>
                <Box className={classes.formRow}>
                    <TextField
                        select
                        required
                        margin="normal"
                        label="Categoria"
                        value={categoriaId}
                        onChange={this.handleCategoriaChange}
                        fullWidth
                        disabled={loadingCategorias}
                    >
                        <MenuItem value="">
                            <em>{loadingCategorias ? 'Carregando...' : 'Selecione a categoria'}</em>
                        </MenuItem>
                        {categorias.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                                {cat.nome}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        required
                        margin="normal"
                        label="Produto"
                        value={produtoId}
                        onChange={this.handleProdutoChange}
                        fullWidth
                        disabled={!categoriaId || loadingProdutos}
                    >
                        <MenuItem value="">
                            <em>{!categoriaId ? 'Selecione a categoria' : loadingProdutos ? 'Carregando...' : 'Selecione o produto'}</em>
                        </MenuItem>
                        {produtos.map((prod) => (
                            <MenuItem key={prod.id} value={prod.id}>
                                {prod.nome}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        required
                        margin="normal"
                        label="Serviço / Equipamento"
                        value={servicoId}
                        onChange={this.handleServicoChange}
                        fullWidth
                        disabled={!produtoId || loadingServicos}
                    >
                        <MenuItem value="">
                            <em>{!produtoId ? 'Selecione o produto' : loadingServicos ? 'Carregando...' : 'Selecione o serviço'}</em>
                        </MenuItem>
                        {servicos.map((svc) => (
                            <MenuItem key={svc.id} value={svc.id}>
                                {svc.nome}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                {/* Quantity, Model, Brand */}
                <Typography className={classes.sectionTitle}>
                    {lang.workshop_quick_details_title}
                </Typography>
                <Box className={classes.formRow}>
                    <TextField
                        margin="normal"
                        label={lang.workshop_quick_quantity}
                        type="number"
                        inputProps={{ min: 1 }}
                        value={quantity}
                        onChange={(e) => this.updateField('quantity', e.target.value)}
                        style={{ maxWidth: 120 }}
                    />
                    <TextField
                        margin="normal"
                        label={lang.workshop_quick_model}
                        value={model}
                        inputProps={{ maxLength: 100 }}
                        onChange={(e) => this.updateField('model', e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        label={lang.workshop_quick_brand}
                        value={brand}
                        inputProps={{ maxLength: 100 }}
                        onChange={(e) => this.updateField('brand', e.target.value)}
                    />
                </Box>

                {/* Observations */}
                <TextField
                    margin="normal"
                    label={lang.workshop_quick_observacoes}
                    value={observacoes}
                    onChange={(e) => this.updateField('observacoes', e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    placeholder={lang.workshop_quick_observacoes_placeholder}
                />
            </DialogContent>
        );
    };

    renderSubmitting = () => {
        const { classes, currentLanguage } = this.props;
        const lang = currentLanguage.bepiService.workshop;
        return (
            <Box className={classes.submittingBox}>
                <CircularProgress size={48} style={{ color: '#1b5e20' }} />
                <Typography variant="h6">{lang.workshop_quick_creating}</Typography>
            </Box>
        );
    };

    renderActions = () => {
        const { currentLanguage } = this.props;
        const { activeStep, companyView, submitting } = this.state;
        const lang = currentLanguage.bepiService.workshop;

        if (submitting) return null;

        return (
            <DialogActions>
                <CustomButton variant="grayOutlined" onClick={this.handleClose}>
                    {lang.workshop_quick_cancel}
                </CustomButton>
                {activeStep === STEPS.COMPANY_STEP && companyView === COMPANY_VIEWS.CREATE_NEW && (
                    <CustomButton variant="greenFilled" onClick={this.handleCreateCompanySubmit}>
                        {this.props.mode === 'new' ? (lang.workshop_quick_register || 'Cadastrar') : lang.workshop_quick_next}
                    </CustomButton>
                )}
                {activeStep === STEPS.COMPANY_STEP && companyView === COMPANY_VIEWS.SELECT_EXISTING && (
                    <CustomButton variant="greenFilled" onClick={this.handleSelectExistingSubmit}>
                        {lang.workshop_quick_continue || 'Continuar'}
                    </CustomButton>
                )}
                {activeStep === STEPS.QUOTATION_STEP && (
                    <CustomButton variant="greenFilled" onClick={this.handleQuotationSubmit}>
                        {lang.workshop_quick_finish}
                    </CustomButton>
                )}
            </DialogActions>
        );
    };

    render() {
        const { open, currentLanguage, classes } = this.props;
        const { activeStep, companyView, submitting } = this.state;
        const lang = currentLanguage.bepiService.workshop;

        const stepLabels = this.props.mode === 'new'
            ? [lang.workshop_quick_step_company]
            : [
                lang.workshop_quick_step_company,
                lang.workshop_quick_step_quotation,
            ];

        return (
            <Dialog open={open} onClose={this.handleClose} fullWidth maxWidth="md">
                <Box className={classes.dialogTitle}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        {this.props.title || lang.workshop_quick_title}
                    </Typography>
                </Box>

                <Stepper activeStep={activeStep} className={classes.stepperRoot}>
                    {stepLabels.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {submitting
                    ? this.renderSubmitting()
                    : activeStep === STEPS.COMPANY_STEP
                        ? (companyView === COMPANY_VIEWS.CHOICE ? this.renderCompanyChoice()
                            : companyView === COMPANY_VIEWS.CREATE_NEW ? this.renderCreateCompanyForm()
                                : this.renderSelectExistingForm())
                        : this.renderQuotationForm()}

                {this.renderActions()}
            </Dialog>
        );
    }
}

export default withStyles(styles)(QuickCompanyModal);
